import { API, postJSON } from "../../api.js";
import { optionsCache } from "../../app/shell.js";
import { renderOrgChartSVG } from "../../charts/org.js";
import { $, esc, fmtMoney, opt } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { openAmountModal, sliderField, toast } from "../../ui/modals.js";

// ---------------------------------------------------------------- Executives

async function renderExecutives(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">Team &amp; Talent — Month ${snap.month} ${helpIcon("executives")}</h2>
    <div class="plain-english-card" id="team-plain-english">Loading…</div>

    <div id="morale-summary"></div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:24px 0 6px;">Organization Chart</h3>
    <p style="font-size:12px; color:var(--text-low); margin-bottom:14px;">Headcount tracks what each department is actually spending and distributes realistically across a real pyramid (Junior → Director). New hires join through a hiring pipeline with genuine lead time — they don't appear overnight.</p>
    <div id="org-chart-svg" class="chart-wrap" style="height:auto; overflow-x:auto;"></div>
    <div id="org-chart">Loading…</div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 6px;">Hiring Pipeline</h3>
    <div id="pipeline-table">Loading…</div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 12px;">Executive Team &amp; Talent Market</h3>
    <p style="font-size:12px; color:var(--text-low); margin-bottom:14px;">VP and C-suite seats now fill automatically once the company has genuinely grown into needing them — Hire Executive is still there for picking someone specific ahead of that, or for a role auto-hire doesn't cover.</p>
    <div class="commands">
      <button class="primary" id="btn-hire-exec">Hire Executive</button>
    </div>
    <div id="exec-table">Loading…</div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 6px;">Corporate Politics ${helpIcon("politics")}</h3>
    <p style="font-size:12px; color:var(--text-low); margin-bottom:14px;">Every hire forms a real relationship with everyone already on the team — allies, rivals, or neutral — and it feeds a real Team Cohesion score into the Board's confidence.</p>
    <div id="politics-summary">Loading…</div>
    <div id="politics-rows">Loading…</div>
  `;

  const [orgData, pipeline, execs, tm, network] = await Promise.all([
    fetch(`${API}/org-chart`).then((r) => r.json()),
    fetch(`${API}/hiring-pipeline`).then((r) => r.json()),
    fetch(`${API}/executives`).then((r) => r.json()),
    fetch(`${API}/talent-market`).then((r) => r.json()),
    fetch(`${API}/executives/network`).then((r) => r.json()),
  ]);
  const flightRiskByName = {};
  tm.executives.forEach((e) => { flightRiskByName[e.name] = e.flight_risk_pct; });

  const moraleCls = orgData.morale >= 70 ? "pos" : orgData.morale >= 45 ? "" : "neg";
  const totalHeadcount = orgData.people ? orgData.people.total : orgData.levels.reduce((sum, l) => sum + l.headcount, 0) + execs.length;
  const atRisk = tm.executives.filter((e) => e.flight_risk_pct >= 15);
  $("team-plain-english").innerHTML =
    `Company morale is ${orgData.morale.toFixed(0)}/100 (${orgData.morale >= 70 ? "healthy" : orgData.morale >= 45 ? "okay" : "concerning — attrition risk is elevated"}).
     You have <strong>${totalHeadcount.toLocaleString("en-US")} total employees</strong>, including ${execs.length} named executive${execs.length === 1 ? "" : "s"}.
     Salaries have drifted ${((orgData.salary_inflation_index - 1) * 100).toFixed(1)}% above founding levels from inflation.
     Rivals are currently poaching at ${tm.rival_talent_pressure_pct}% intensity${atRisk.length ? ` — ${atRisk.length} exec${atRisk.length > 1 ? "s are" : " is"} at real risk of leaving this month.` : ", but none of your executives are at meaningful risk right now."}`;
  $("morale-summary").innerHTML = `
    <table class="ledger">
      <tr><td class="label">Total Employees</td><td class="mono">${totalHeadcount.toLocaleString("en-US")}</td></tr>
      <tr><td class="label">Company Morale</td><td class="mono ${moraleCls}">${orgData.morale.toFixed(0)}/100 ${orgData.morale < 45 ? "⚠️ elevated attrition risk" : ""}</td></tr>
      <tr><td class="label">Salary Inflation Index</td><td class="mono">${orgData.salary_inflation_index.toFixed(3)}x since founding</td></tr>
      <tr><td class="label">Rival Poaching Pressure</td><td class="mono">${tm.rival_talent_pressure_pct}%</td></tr>
    </table>
  `;

  // --- Org chart: department x level grid --------------------------------------
  const deptGroups = {};
  orgData.levels.forEach((l) => {
    deptGroups[l.department] = deptGroups[l.department] || [];
    deptGroups[l.department].push(l);
  });

  const svgHost = $("org-chart-svg");
  if (svgHost) svgHost.innerHTML = renderOrgChartSVG(deptGroups, orgData.morale);

  $("org-chart").innerHTML = Object.entries(deptGroups).map(([dept, levels]) => {
    const deptTotal = levels.reduce((s, l) => s + l.headcount, 0);
    const deptCost = levels.reduce((s, l) => s + l.annual_cost, 0);
    return `
      <table class="ledger" style="margin-bottom:14px;">
        <tr class="section"><td class="label">${esc(dept)} — ${deptTotal.toLocaleString("en-US")} people</td><td class="mono">${fmtMoney(deptCost)}/yr</td></tr>
        ${levels.map((l) => `
          <tr>
            <td class="label">${esc(l.level)}</td>
            <td class="mono">${l.headcount.toLocaleString("en-US")} @ ${fmtMoney(l.avg_annual_salary)} = ${fmtMoney(l.annual_cost)}</td>
          </tr>
        `).join("")}
      </table>
    `;
  }).join("") || `<p style="color:var(--text-mid)">No org chart data yet.</p>`;

  // --- Hiring pipeline -----------------------------------------------------------
  $("pipeline-table").innerHTML = pipeline.length ? `
    <table class="ledger">
      <tr><th>Department</th><th>Level</th><th>Headcount</th><th>Joins In</th></tr>
      ${pipeline.map((p) => `
        <tr>
          <td class="label">${esc(p.department)}</td>
          <td class="label">${esc(p.level)}</td>
          <td class="mono">${p.headcount}</td>
          <td class="mono">${p.months_until_join === 0 ? "This month" : p.months_until_join + " month(s)"}</td>
        </tr>
      `).join("")}
    </table>
  ` : `<p style="color:var(--text-mid)">No hires currently in the pipeline.</p>`;

  $("btn-hire-exec").addEventListener("click", openHireModal);
  const table = $("exec-table");
  if (!execs.length) {
    table.innerHTML = `<p style="color:var(--text-mid)">No named executives hired yet. The board is still just you.</p>`;
    return;
  }
  table.innerHTML = `
    <table class="ledger">
      <tr><th>Role</th><th>Name</th><th>Annual Salary</th><th>Ambition</th><th>Loyalty</th><th>Hired</th><th></th></tr>
      ${execs.map((e) => {
        const flightRiskPct = flightRiskByName[e.name] ?? 0;
        const riskCls = flightRiskPct >= 15 ? "neg" : flightRiskPct >= 6 ? "" : "pos";
        return `
        <tr>
          <td class="label">${esc(e.role)}</td>
          <td class="label">${esc(e.name)} ${flightRiskPct >= 15 ? "⚠️" : ""}</td>
          <td class="mono">${fmtMoney(e.annual_salary)}</td>
          <td class="mono">${e.ambition.toFixed(0)}</td>
          <td class="mono ${riskCls}">${e.loyalty.toFixed(0)} <span style="color:var(--text-low); font-size:11px;">(${flightRiskPct}% flight risk)</span></td>
          <td class="mono">M${e.hired_month}</td>
          <td>
            <button data-retain="${e.id}" data-name="${esc(e.name)}">Retention Bonus</button>
            <button data-id="${e.id}" class="btn-fire">Let go</button>
          </td>
        </tr>
      `}).join("")}
    </table>
  `;
  table.querySelectorAll(".btn-fire").forEach((btn) => {
    btn.addEventListener("click", async () => {
      await postJSON("/executives/fire", { executive_id: parseInt(btn.dataset.id) });
      await window.refreshAll();
      toast("Executive departed. Severance paid.");
    });
  });
  table.querySelectorAll("[data-retain]").forEach((btn) => {
    btn.addEventListener("click", () => openAmountModal(`Retention Bonus — ${btn.dataset.name}`,
      "Bonus amount ($)? (~$4,000 buys 1 loyalty point)", async (amount) => {
        const res = await postJSON("/executives/retention-bonus", { executive_id: parseInt(btn.dataset.retain), amount });
        await window.refreshAll();
        toast(res.message);
      }, false, 25000));
  });

  // --- Corporate Politics: relationship network + team cohesion ------------------
  const cohesion = network.team_cohesion_score;
  const cohesionCls = cohesion >= 65 ? "pos" : cohesion >= 40 ? "" : "neg";
  $("politics-summary").innerHTML = `
    <div class="risk-stat-row">
      <div class="risk-stat-box">
        <div class="risk-stat-label">Team Cohesion</div>
        <div class="risk-stat-value ${cohesionCls}">${cohesion}/100</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Tracked Relationships</div>
        <div class="risk-stat-value">${network.relationships.length}</div>
      </div>
    </div>
  `;
  $("politics-rows").innerHTML = network.relationships.length ? network.relationships.map((r) => `
    <div class="risk-row">
      <div class="risk-row-left">
        <div class="risk-row-label">${esc(r.a_name)} (${esc(r.a_role)}) ↔ ${esc(r.b_name)} (${esc(r.b_role)})</div>
        <div class="risk-row-sub">Strength ${r.strength}/100</div>
      </div>
      <span class="flight-risk-badge ${r.relationship_type === "Ally" ? "low" : r.relationship_type === "Rival" ? "high" : "med"}">${esc(r.relationship_type)}</span>
    </div>
  `).join("") : `<p style="color:var(--text-mid)">Hire a second executive to start forming real relationships.</p>`;
}

function openHireModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>Hire Executive</h3>
      <div class="field"><label>Role</label><select id="modal-role">${opt(optionsCache.executive_roles)}</select></div>
      <div class="field"><label>Name</label><input id="modal-name" placeholder="e.g. Dana Whitfield" /></div>
      ${sliderField("modal-salary", "Annual salary", { min: 40000, max: 1000000, step: 5000, value: 180000 })}
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">Hire</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const role = backdrop.querySelector("#modal-role").value;
    const name = backdrop.querySelector("#modal-name").value.trim() || "Unnamed Executive";
    const salary = parseFloat(backdrop.querySelector("#modal-salary").value) || 0;
    backdrop.remove();
    if (salary <= 0) return;
    await postJSON("/executives/hire", { role, name, annual_salary: salary });
    await window.refreshAll();
    toast(`${name} hired as ${role}.`);
  });
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.renderExecutives = renderExecutives;
